<template>
  <h2 v-if="label && !hideLabel">{{ label }}</h2>
  <div v-if="errorMessage" ref="errorMessageRef" class="alert alert-danger my-2">
    <span><i :class="[ICONS.ErrorCircle, 'color-danger', '']"></i> {{ errorMessage }}</span>
  </div>
  <FileUpload
    ref="fileUploadRef"
    :name="name"
    :auto="true"
    :show-upload-button="false"
    :show-cancel-button="false"
    :multiple="fileLimit > 1"
    :file-limit="fileLimit"
    :accept="accept"
    :entity="entity"
    :network="network"
    :examProduct="examProduct"
    :qaModuleType="qaModuleType"
    :url="url"
    :hideLabel="hideLabel"
    custom-upload
    @uploader="onUpload"
    :pt:input:aria-label="label"
    :choose-button-props="{'aria-label': 'Choose ' + (label ?? name)}"
    :upload-button-props="{'aria-label': 'Upload ' + (label ?? name)}"
    :cancel-button-props="{'aria-label': 'Cancel ' + (label ?? name)}"
    @removeUploadedFile="removeFile"
    @cancel="clear"
    @reset="clear"
  >
    <template #empty>
      <p class="dropzone">Drag and drop files here or select choose above.<br/>The selected file will be automatically renamed and uploaded.</p>
    </template>
  </FileUpload>
</template>

<script>
import {getPreSignedUrl} from '@/services/uploadFile.js';
import FileUpload from 'primevue/fileupload';
import axios from "axios";
import {useAppStore} from "@/stores/appStore.js";
import {ICONS} from "@/utils/constants.js";
import {DocumentModel} from "@/models/DocumentModel.js";
import {getDocuments, saveDocument, saveEditDocument} from "@/services/documentDBservice.js";
import {useAuthStore} from "@/stores/authStore.js";

export default {
  computed: {
    ICONS() {
      return ICONS;
    },
  },
  components: {
    FileUpload
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    url: {
      type: String,
      default: '/undefined',
    },
    accept: {
      type: String,
      default: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    entity: {
      type: Number,
      required: false,
    },
    network: {
      type: Number,
      required: false,
    },
    examProduct: {
      type: Number,
      required: false,
    },
    label: {
      type: String,
      required: false,
    },
    moduleType: {
      type: String,
      required: false,
    },
    qaModuleType: {
      type: String,
      required: false,
    },
    qaModuleTypeDescription: {
      type: String,
      required: false,
    },
    ref: {
      type: String,
      required: false,
    },
    index: {
      type: Number,
      required: false,
    },
    fileLimit: {
      type: Number,
      required: false,
      default: 1,
    },
    hideLabel: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:url'],
  data() {
    return {
      url: '/initial-url',  // This is the URL that will be passed down
      errorMessage: '',
      ref: '',
    };
  },
  setup() {
    const appStore = useAppStore();
    const authStore = useAuthStore();

    return {
      appStore,authStore
    };
  },
  methods: {
    async onUpload(event) {
      // PrimeVue FileUpload triggers this event for each file with event.files containing that file
      const filesToUpload = event.files || [];
      console.log('onUpload called with files:', filesToUpload.map(f => f.name));

      // Process files one by one
      for (let i = 0; i < filesToUpload.length; i++) {
        await this.processFile(filesToUpload[i], i, filesToUpload.length);
      }

      // Clear ALL pending files at once after the batch completes
      this.clearPendingFiles();
    },

    async processFile(file, index, totalFiles) {
      console.log(`Processing file ${index + 1}/${totalFiles}:`, file.name);

      const renamedFile = await this.prepareFileForUpload(file, index);
      if (!renamedFile) {
        return; // Error already handled in prepareFileForUpload
      }

      await this.uploadFileToS3(renamedFile);
    },

    async prepareFileForUpload(file, index) {
      try {
        // Pass index to ensure uniqueness even if timestamps collide
        const renamedFile = await this.renameFile(file, index);

        this.url = (await getPreSignedUrl(renamedFile)).url;
        this.$emit('update:url', this.url);
        this.errorMessage = '';

        return renamedFile;
      } catch (error) {
        console.error('Pre-signed URL error for', file.name, error);
        this.handleError('Failed to get upload URL for ' + file.name + '. Please try again.');
        return null;
      }
    },

    async uploadFileToS3(renamedFile) {
      try {
        this.$refs.fileUploadRef.progress = 0;
        await axios.put(this.url, renamedFile, {
          headers: {
            "Content-Type": renamedFile.type
          },
          onUploadProgress: (uploadEvent) => {
            if (uploadEvent.lengthComputable) {
              this.$refs.fileUploadRef.progress = Math.round((uploadEvent.loaded / uploadEvent.total) * 100);
            }
          }
        });
        this.$refs.fileUploadRef.progress = 100;

        console.log('Upload to S3 successful:', renamedFile.name);

        await this.finalizeFileUpload(renamedFile);
      } catch (error) {
        console.error('Upload error for', renamedFile.name, error);
        this.handleError('Upload failed for ' + renamedFile.name + ': ' + error.message);
      }
    },

    async finalizeFileUpload(renamedFile) {
      this.errorMessage = '';
      await this.saveDocumentData(renamedFile);
      console.log('Document data saved:', renamedFile.name);

      // Add to uploaded files
      this.$refs.fileUploadRef.uploadedFiles.push(renamedFile);
      console.log('Added to uploadedFiles. Total uploaded:', this.$refs.fileUploadRef.uploadedFiles.length);
    },

    clearPendingFiles() {
      // This is safer than trying to remove individual files during the loop
      console.log('Clearing pending files array');
      this.$refs.fileUploadRef.files = [];
    },

    async renameFile(file, index = 0) {
      const fileType = file.type;
      const { fileExtension, originalName } = this.parseFileName(file.name);

      const newFileName = this.buildNewFileName(originalName, fileExtension, index);

      console.log("Original filename:", file.name);
      console.log("Renamed to:", newFileName);

      return this.createRenamedFile(file, newFileName, fileType);
    },

    parseFileName(fileName) {
      const nameParts = fileName.split('.');
      const fileExtension = nameParts.pop();
      const originalName = nameParts.join('.');

      return { fileExtension, originalName };
    },

    buildNewFileName(originalName, fileExtension, index) {
      let newFileName = this.name;

      newFileName = this.appendEntityToFileName(newFileName);
      newFileName = this.appendNetworkToFileName(newFileName);
      newFileName = this.appendExamProductToFileName(newFileName);
      newFileName = this.appendTimestampAndIndex(newFileName, index);
      newFileName = this.appendOriginalName(newFileName, originalName);

      return newFileName + '.' + fileExtension;
    },

    appendEntityToFileName(fileName) {
      if (this.entity != null) {
        const entityName = this.appStore.getEntityNameById(this.entity);
        return fileName + '_' + this.getShortName(entityName);
      }
      return fileName;
    },

    appendNetworkToFileName(fileName) {
      if (this.network != null) {
        const networkId = this.appStore.getNetworkIdById(this.network);
        if (networkId) {
          return fileName + '_' + this.getShortName(networkId);
        }
      }
      return fileName;
    },

    appendExamProductToFileName(fileName) {
      if (this.examProduct != null) {
        const examProduct = this.appStore.getExamProductById(this.examProduct);
        if (examProduct && examProduct.id && examProduct.productName) {
          fileName = fileName + '_' + examProduct.id + '_' + this.sanitizeFileName(examProduct.productName);
        }
      }
      if (this.qaModuleType != null) {
         fileName = fileName + '_' + this.qaModuleType;
      }
      return fileName;
    },

    appendTimestampAndIndex(fileName, index) {
      // Include both timestamp AND index for guaranteed uniqueness
      return fileName + '_' + Date.now() + '_' + index;
    },

    appendOriginalName(fileName, originalName) {
      if (originalName != null) {
        return fileName + '_' + this.getShortName(originalName);
      }
      return fileName;
    },

    createRenamedFile(file, newFileName, fileType) {
      const renamedFile = new File([file], newFileName, { type: fileType });
      // Store original filename for reference
      renamedFile.originalName = file.name;

      return renamedFile;
    },

    async getUploadedFiles() {
      const fileUpload = this.$refs.fileUploadRef;
      if (fileUpload && fileUpload.uploadedFiles) {
        return fileUpload.uploadedFiles;
      }
      return [];
    },

    getShortName(name) {
      if (!name) {
        return '';
      }
      return String(name)
          .slice(0, 9)
          .replace(/[^a-zA-Z0-9.-]+/g, '-')
          .replace(/^_+|_+$/g, '');
    },

    sanitizeFileName(name) {
      if (!name) {
        return '';
      }
      // Replace spaces and special characters (except hyphens and periods) with underscores
      // Remove leading/trailing underscores
      return String(name)
          .replace(/\s+/g, '_')
          .replace(/[^a-zA-Z0-9._-]+/g, '_')
          .replace(/^_+|_+$/g, '');
    },


    async saveDocumentData(fileData){
      let docToSave = new DocumentModel();
      if(this.moduleType?.includes('Document Management Table')) {
        let docList = await getDocuments();
        let filteredDocs = docList.filter(doc => doc.id === this.index);
        let docToSave = filteredDocs.reduce((prev, current) => {
          return (prev.version > current.version) ? prev : current;
        }, filteredDocs[0]);
        docToSave.version = docToSave.version + 1;
        docToSave.uploaderName = fileData.name.split('_').shift();
        docToSave.categoryOfUpload = fileData.name.split('.').pop();
        docToSave.documentName = fileData.name;
        docToSave.editorFullName = this.authStore.user.fullName;
        docToSave.editorEmail = this.authStore.user.email;

        console.log('Sending document to backend with version:', docToSave.version, 'id:', docToSave.id);
        await saveEditDocument(docToSave);
      } else if (this.moduleType?.includes('External Appeal Supporting Documents')) {
        // NO-OP - Handled via form submission
      } else if (this.moduleType?.includes('Consumer Complaint Supporting Documents')) {
        // NO-OP - Handled via form submission
      } else {
        docToSave.version = 1;
        docToSave.moduleType = this.moduleType;
        docToSave.uploaderName = fileData.name.split('_').shift();
        docToSave.categoryOfUpload = fileData.name.split('.').pop();
        docToSave.documentApproved = false;
        docToSave.isDeleted = false;
        docToSave.documentName = fileData.name;
        docToSave.acceptedFileTypes = this.accept;
        docToSave.label = this.label;
        docToSave.refName = this.name;

        // For QA uploads, get entity from examProduct
        if (this.examProduct != null) {
          const examProductDetails = this.appStore.getExamProductById(this.examProduct);
          if (examProductDetails && examProductDetails.entityId) {
            docToSave.entityUniqueId = examProductDetails.entityId;
            docToSave.entityName = this.appStore.getEntityNameById(examProductDetails.entityId);
          }
          docToSave.examProductId = this.examProduct;
        } else if (this.entity != null) {
          docToSave.entityUniqueId = this.entity;
          docToSave.entityName = this.appStore.getEntityNameById(this.entity);
        }

        // Save QA module type information
        if (this.qaModuleType != null) {
          docToSave.qaModuleType = this.qaModuleType;
        }
        if (this.qaModuleTypeDescription != null && this.qaModuleTypeDescription.trim() !== '') {
          docToSave.qaModuleTypeDescription = this.qaModuleTypeDescription;
        }

        if (this.network != null) {
          docToSave.networkUniqueId = this.network;
          docToSave.networkId = this.appStore.getNetworkIdById(this.network);
          docToSave.networkName = this.appStore.getNetworkNameById(this.network);
        }
        docToSave.creatorFullName = this.authStore.user.fullName;
        docToSave.creatorEmail = this.authStore.user.email;
        await saveDocument(docToSave);
      }
    },

    clear() {
      this.$refs.fileUploadRef.uploadedFileCount = 0;
      this.$refs.fileUploadRef.files = [];
      this.$refs.fileUploadRef.messages = [];
      this.$refs.fileUploadRef.focused = false;
      this.$refs.fileUploadRef.progress = 0;
      this.$refs.fileUploadRef.uploadedFiles = [];
      // This clear function from the component doesn't do everything it should so also setting default state above.
      this.$refs.fileUploadRef.clear();
    },

    removeFile(event) {
      // The event contains the file object that was removed
      // PrimeVue already handles removing it from uploadedFiles
      const fileUpload = this.$refs.fileUploadRef;
      if (fileUpload && fileUpload.uploadedFiles) {
        // If this was the last file, do a full clear to reset component state
        if (fileUpload.uploadedFiles.length === 0) {
          this.clear();
        } else {
          // Otherwise just update the count
          fileUpload.uploadedFileCount = fileUpload.uploadedFiles.length;
        }
      }
    },

    handleError(message) {
      this.errorMessage = message || 'Unexpected error occurred.';

      // Delay is important to let DOM render first
      this.$nextTick(() => {
        this.$refs.errorMessageRef?.$el?.focus();
      });
    },
  }
};
</script>

<style scoped>

</style>